import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { FileText, Loader2 } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      {/* Left brand panel */}
      <div className="hidden w-1/2 flex-col justify-between bg-cass-navy p-12 lg:flex">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-md bg-cass-blue">
            <FileText className="h-6 w-6 text-white" />
          </div>
          <span className="font-head text-xl font-bold text-white">CASS Dividends</span>
        </div>
        <div>
          <h1 className="font-head text-4xl font-bold leading-tight text-white">
            Dividend Voucher Tool
          </h1>
          <p className="mt-4 max-w-md text-white/60">
            Store company and shareholder records, calculate variable monthly dividend
            rates with exact precision, and generate professionally branded PDF vouchers.
          </p>
        </div>
        <p className="text-xs text-white/40">Internal administration tool — authorised users only.</p>
      </div>

      {/* Right form */}
      <div className="flex w-full items-center justify-center bg-white px-6 lg:w-1/2">
        <div className="w-full max-w-sm">
          <div className="mb-8 lg:hidden">
            <div className="flex items-center gap-2">
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-cass-blue">
                <FileText className="h-5 w-5 text-white" />
              </div>
              <span className="font-head text-lg font-bold text-cass-navy">CASS Dividends</span>
            </div>
          </div>
          <h2 className="font-head text-2xl font-bold text-cass-navy">Sign in</h2>
          <p className="mb-6 mt-1 text-sm text-cass-navy/60">Enter your credentials to continue.</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email address</Label>
              <Input id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                data-testid="login-email" placeholder="you@cass-online.co.uk" required
                className="h-10 border-cass-border focus-visible:ring-cass-blue" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password" placeholder="••••••••" required
                className="h-10 border-cass-border focus-visible:ring-cass-blue" />
            </div>
            {error && <p data-testid="login-error" className="text-sm text-destructive">{error}</p>}
            <Button type="submit" disabled={loading} data-testid="login-submit"
              className="h-10 w-full bg-cass-blue text-white hover:bg-cass-bluehover">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Sign in
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}
